import type { ReactNode } from 'react';
import { cn, tv } from '../_lib/utils';
import type { SanityImage } from '../types';
import { isValidRichText } from '../_lib/isValidRichText';
import { Author, type AuthorProps } from '../Author';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { IconResolver, type IconType } from '../IconResolver';
import { Media } from '../Media';
import { Text } from '../Text';
import { Stat, type StatisticProps } from '../Stat';

const styles = tv({
	slots: {
		listIcon: 'text-text-brand w-4 h-4',
	},
	variants: {
		size: {
			lg: {
				listIcon: 'w-5 h-5',
			},
			md: {},
			sm: {},
		},
	},
});

type TextSectionBlockListItemProps = {
	icon?: IconType;
	text: string;
};

export type TextSectionBlockProps = {
	align?: 'center' | 'left';
	buttons?: ComponentButtonProps[];
	className?: string;
	copy?: ReactNode;
	labelText?: {
		label?: string;
		copy?: ReactNode;
	};
	listItems?: {
		label?: string;
		items: TextSectionBlockListItemProps[];
	};
	logo?: SanityImage;
	overline?: string;
	quote?: {
		copy: ReactNode;
		author?: AuthorProps;
	};
	stats?: Omit<StatisticProps, 'size' | 'styleType'>[];
	title?: ReactNode;
	size?: 'sm' | 'md' | 'lg';
};

export function TextSectionBlock(props: TextSectionBlockProps) {
	const align = props.align ?? 'left';
	const size = props.size ?? 'lg';
	const { listIcon } = styles({ size });

	return (
		<article
			className={cn('@container w-full flex flex-col gap-6', align === 'center' ? 'text-center' : '', props.className)}
			data-component='TextSectionBlock'
		>
			<div className={`flex flex-col gap-4 empty:hidden`}>
				{props.overline && (
					<Text as='span' className='text-text-brand' styleType='label-md'>
						{props.overline}
					</Text>
				)}

				{props.title && (
					<Text as='h2' styleType={getTitleSize(size)}>
						{props.title}
					</Text>
				)}

				{isValidRichText(props.copy) && (
					<Text className={`opacity-80`} styleType={getCopySize(size)}>
						{props.copy}
					</Text>
				)}
			</div>

			{props.listItems && (
				<div>
					{props.listItems?.label && (
						<Text as='h3' className={`py-spacing-3xs`} styleType='label-md'>
							{props.listItems?.label}
						</Text>
					)}
					{props.listItems?.items && props.listItems?.items.length > 0 && (
						<ul>
							{props.listItems?.items.map((item, index) => (
								<Text
									as='li'
									className={`flex items-center gap-2 opacity-80 py-responsive-3xs`}
									styleType={size === 'lg' ? 'body-lg' : 'body-md'}
									key={`text-section-block-list-item-${index}-${item.text}`}
								>
									{item.icon && <IconResolver className={listIcon()} icon={item.icon} />}
									{item.text}
								</Text>
							))}
						</ul>
					)}
				</div>
			)}

			{props.labelText && (
				<div>
					{props.labelText?.label && (
						<Text as='h3' className={`py-spacing-3xs`} styleType='label-md'>
							{props.labelText?.label}
						</Text>
					)}
					{isValidRichText(props.copy) && (
						<Text className={`opacity-80`} styleType={size === 'lg' ? 'body-lg' : 'body-md'}>
							{props.copy}
						</Text>
					)}
				</div>
			)}

			{props.logo && <Media className={`aspect-video max-w-[15rem] [&_img]:h-full [&_img]:object-fit!`} image={props.logo} />}

			{props.quote && (
				<div className={`py-responsive-3xs`}>
					<div className={`flex flex-col gap-3 pl-4 border-l border-background-brand`}>
						<Text
							as='blockquote'
							className={`opacity-80 [&>*]:open-quote [&>*]:close-quote`}
							styleType={size === 'lg' ? 'body-lg-medium' : 'body-md-medium'}
						>
							{props.quote.copy}
						</Text>
						{props.quote?.author && (
							<figcaption className={`flex`}>
								<Author
									alignment='left'
									avatarMobile={false}
									image={props.quote?.author?.image}
									name={props.quote?.author?.name}
									meta={props.quote?.author?.meta}
								/>
							</figcaption>
						)}
					</div>
				</div>
			)}

			{props.stats && props.stats.length > 0 && (
				<div className='grid gap-responsive-lg @xl:grid-cols-2'>
					{props.stats.map((item, i) => (
						<Stat key={`${item.label}-${item.value}-${i}-text-section-block-stat`} {...item} size='medium' />
					))}
				</div>
			)}

			{props.buttons && props.buttons.length > 0 && (
				<div className={cn('flex flex-wrap gap-2', align === 'center' ? 'justify-center' : '')}>
					{props.buttons.map((item, index) => (
						<ComponentButton key={`${item.label}-${index}-text-section-block-button`} {...item} />
					))}
				</div>
			)}
		</article>
	);
}

function getTitleSize(size: TextSectionBlockProps['size']) {
	switch (size) {
		case 'lg':
			return 'heading-lg';
		case 'md':
			return 'heading-md';
		case 'sm':
			return 'heading-sm';
		default:
			return 'heading-lg';
	}
}

function getCopySize(size: TextSectionBlockProps['size']) {
	switch (size) {
		case 'lg':
			return 'body-lg';
		case 'md':
			return 'body-md';
		case 'sm':
			return 'body-md';
		default:
			return 'body-md';
	}
}
